import React from 'react';

import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import { AICommandIcon } from '@atlaskit/editor-toolbar';
import { fg } from '@atlaskit/platform-feature-flags';

import { IssueReformatterIcon } from '../../../../icons/prompt/issueReformatter';
import { jiraIssueReformatterApiURL } from '../../../utils';
import { createInsertBelow, createReplace } from '../../actions/create-actions';
import { CONFIG_ITEM_KEYS } from '../../config-item-keys';
import type {
	EditorPluginAIConfigItemFactoryOptions,
	EditorPluginAIConfigItemWithOptions,
	EditorPluginAIConfigItem,
} from '../../config-items';

export type GetAdditionalProps = () => Partial<{
	issue_id: string;
	issue_key?: string;
	issue_summary?: string;
	issue_type_label?: string;
	linked_confluence_page_urls?: string[];
	linked_urls?: string[];
	project_id?: string;
}>;

type Options = EditorPluginAIConfigItemFactoryOptions & {
	getAdditionalProps: GetAdditionalProps;
};

const getIssueType = (issueType: string | undefined) => {
	switch (issueType) {
		case 'bug':
			return 'Bug';
		case 'story':
			return 'Story';
		case 'epic':
			return 'Epic';
		case 'task':
		default:
			return 'Task'; // Default to 'Task' if issueType is not recognized
	}
};

export const createImproveDescription = (options: Options): EditorPluginAIConfigItem => ({
	key: CONFIG_ITEM_KEYS.ADD_STRUCTURE,
	title: aiConfigItemMessages.improveDescription,
	description: aiConfigItemMessages.reformatWithTemplateDescription,
	icon: ({ formatMessage }) =>
		options.isInDocumentStreamingEnabled ? (
			<AICommandIcon label={formatMessage(aiConfigItemMessages.improveDescription)} size="small" />
		) : (
			<IssueReformatterIcon />
		),
	endpoint: jiraIssueReformatterApiURL,
	experienceId: 'issue-reformatter',
	intentSchemaId: 'issue_reformatter_intent_schema.json',
	featureId: 'issue_reformatter',
	alwaysNeedDoc: true,
	getRequestPayload: ({ locale, doc }) => {
		// additional props come from Issue View
		const additionalProps = options.getAdditionalProps?.() || {};
		const {
			issue_id,
			issue_summary,
			project_id,
			issue_type_label,
			linked_urls,
			linked_confluence_page_urls,
		} = additionalProps;

		// Only include Confluence page URLs if feature gate is enabled
		// Note: Feature gate check must be last to ensure exposure tracking
		const shouldIncludeConfluenceUrls =
			linked_confluence_page_urls &&
			linked_confluence_page_urls.length > 0 &&
			fg('linked_confluence_page_context_enabled');

		// all context is required except linked_urls and linked_confluence_page_urls
		const context = {
			project_id,
			// Only include issue_id in the context payload when it is provided, so the
			// reformatter backend only receives it for users in the experiment.
			...(issue_id !== undefined ? { issue_id } : {}),
			summary: issue_summary,
			description: doc,
			// @ts-ignore - TS2869 TypeScript 5.9.2 upgrade
			...({ links: linked_urls } ?? []),
			// Add linked_confluence_page_urls to context only if feature gate is enabled
			...(shouldIncludeConfluenceUrls ? { linked_confluence_page_urls } : {}),
		};
		const issue_type = getIssueType(issue_type_label);

		// see https://hello.atlassian.net/wiki/spaces/AIDO/pages/5306581134/PWDC+Issue+Reformatter+API+Contracts
		const ai_feature_input = {
			source: 'JIRA',
			locale,
			context,
			response_type: 'NO_SCHEMA',
			suggested_issues_config: {
				use_template: true,
				max_issues: 1,
				suggested_issue_field_types: [
					{
						issue_type,
						fields: [{ field_name: 'Description', field_type: 'Paragraph' }],
					},
				],
			},
		};

		return { ai_feature_input };
	},
	actions: {
		empty: [
			createInsertBelow({
				appearance: 'secondary',
				factoryOptions: options as EditorPluginAIConfigItemFactoryOptions,
			}),
			createReplace({ appearance: 'primary' }),
		],
		selection: [
			createInsertBelow({
				appearance: 'secondary',
				factoryOptions: options as EditorPluginAIConfigItemFactoryOptions,
			}),
			createReplace({ appearance: 'primary' }),
		],
	},
	getBackendModel: () => 'assistance-service',
});

export const createImproveDescriptionWithOptions = (
	options: Options,
): EditorPluginAIConfigItemWithOptions => ({
	config: createImproveDescription(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});
